/**
 * The agents: who can be asked, what each one may touch, and the instructions they run
 * on. Built-in roles ship with the editor; the user adds their own in the panel settings
 * (a name, a model, a prompt, a tool set and optionally their own endpoint), and every
 * one of them is reachable through the coordinator's `delegate` tool.
 */
import type { Catalog } from './agentCatalog';
import type { MaterialEntry } from '../state/materials';
import { RENDER_STYLES } from '../state/aiRender';
import { fmt } from './sceneSummary';
import { TOOL_GROUPS, type ToolGroup } from './toolSchemas';
import { usableCatalogItems } from './catalogFilter';
import { language, t, type LanguageCode } from '../i18n/I18n';

export type Effort = 'low' | 'medium' | 'high' | 'xhigh' | 'max';

export interface AgentDefinition {
  id: string;
  name: string;
  description: string;
  model: string;
  effort?: Effort;
  tools: readonly ToolGroup[] | '*';
  /** Role guidance appended to the shared instructions. */
  role: string;
  /** Stored in this browser rather than shipped with the editor. */
  custom?: boolean;
  /** A custom agent may talk to its own Messages-compatible endpoint with its own key. */
  endpoint?: { baseUrl?: string; apiKey?: string };
}

export const DEFAULT_MODEL = 'claude-opus-5';
export const MODEL_CHOICES: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'claude-opus-5', label: 'Claude Opus 5' },
  { id: 'claude-sonnet-5', label: 'Claude Sonnet 5' },
  { id: 'claude-haiku-4-5', label: 'Claude Haiku 4.5' },
  { id: 'claude-fable-5-1', label: 'Claude Fable 5.1' },
];

/** Built-in roles: their names and descriptions are read in the interface language when shown. */
export const BUILT_IN_AGENTS: readonly AgentDefinition[] = [
  {
    id: 'designer', get name() { return t('#AgentDesignerName'); }, model: DEFAULT_MODEL, tools: '*',
    get description() { return t('#AgentDesignerDescription'); },
    role: 'You are the general interior designer: you plan rooms, place and arrange furniture, choose finishes, manage the view and make renders — everything the editor can do.',
  },
  {
    id: 'architect', get name() { return t('#AgentArchitectName'); }, model: DEFAULT_MODEL, tools: ['read', 'build', 'view', 'history'],
    get description() { return t('#AgentArchitectDescription'); },
    role: 'You are the architect: you build and correct the plan — rooms, walls, doors, windows and arches — and keep the layout buildable. You do not place furniture or choose finishes; say so if asked.',
  },
  {
    id: 'decorator', get name() { return t('#AgentDecoratorName'); }, model: DEFAULT_MODEL, tools: ['read', 'furnish', 'finish', 'view', 'history'],
    get description() { return t('#AgentDecoratorDescription'); },
    role: 'You are the decorator: you furnish rooms, arrange and resize items and choose floor, wall and furniture finishes. You do not move walls or openings; say so if asked.',
  },
  {
    id: 'photographer', get name() { return t('#AgentPhotographerName'); }, model: DEFAULT_MODEL, tools: ['read', 'view', 'render'],
    get description() { return t('#AgentPhotographerDescription'); },
    role: 'You are the visualiser: you frame the 3D view (mode, room, camera), capture pictures and make AI renders in the style the user wants. You do not change the plan or the furniture.',
  },
  {
    id: 'coordinator', get name() { return t('#AgentCoordinatorName'); }, model: DEFAULT_MODEL, tools: ['read', 'view', 'agents'],
    get description() { return t('#AgentCoordinatorDescription'); },
    role: 'You are the coordinator. The map tells you the state; route work: architect for walls, rooms and openings; decorator for furniture and finishes; photographer for views and renders. Order what depends on something else (plan before furniture before render) and run the rest side by side: one delegate call with several tasks — one specialist per room, or the furniture of a room and its finishes to two agents — each brief naming its room and what to leave alone. Report the combined outcome. Answer simple questions yourself.',
  },
];

export const CUSTOM_AGENT_TOOL_GROUPS = TOOL_GROUPS;

export interface AgentSettings {
  agentId: string;
  /** Overrides every built-in agent's model when set. */
  model?: string;
  /** The user's own Anthropic key: the browser then talks to the API directly. */
  apiKey?: string;
  customAgents: AgentDefinition[];
  /** Accept tool calls from external agents over the local MCP bridge. */
  bridge: boolean;
  /** Answer from a scripted stand-in instead of a model (UI checks). */
  mock?: boolean;
  /** Run through the local agent — `npm run agent:local`, Claude Code on the user's subscription. */
  localAgent: boolean;
  localAgentUrl?: string;
  /** Model alias handed to `claude --model`; empty keeps Claude Code's own default. */
  localModel?: string;
  /** Which API the built-in agents talk to: Claude, or an OpenAI-compatible one (DeepSeek and the like). */
  provider: ProviderKind;
  /** True once the user picked a provider; until then the editor follows the keys its server holds. */
  providerChosen?: boolean;
  /** The OpenAI-compatible endpoint; without a key the editor's proxy supplies the server's. */
  openai?: { baseUrl?: string; apiKey?: string; model?: string; /** Let a DeepSeek model think before answering (slower). */ thinking?: boolean };
}

export type ProviderKind = 'claude' | 'openai';
export const PROVIDER_CHOICES: ReadonlyArray<{ id: ProviderKind; label: string }> = [
  { id: 'claude', label: 'Claude' },
  { id: 'openai', get label() { return t('#AgentProviderOpenAICompatible'); } },
];

export const LOCAL_AGENT_URL = 'http://127.0.0.1:5181';
export const LOCAL_MODEL_CHOICES: ReadonlyArray<{ id: string; label: string }> = [
  { id: '', get label() { return t('#AgentLocalModelDefault'); } },
  { id: 'opus', label: 'Opus' },
  { id: 'sonnet', label: 'Sonnet' },
  { id: 'haiku', label: 'Haiku' },
];

const SETTINGS_KEY = 'hp.agent.settings';
const DEFAULT_SETTINGS: AgentSettings = { agentId: 'designer', customAgents: [], bridge: false, localAgent: false, provider: 'claude' };

export function loadAgentSettings(storage: Pick<Storage, 'getItem'> | null = typeof localStorage === 'undefined' ? null : localStorage): AgentSettings {
  try {
    const raw = storage?.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<AgentSettings>;
    return {
      agentId: typeof parsed.agentId === 'string' ? parsed.agentId : DEFAULT_SETTINGS.agentId,
      model: typeof parsed.model === 'string' && parsed.model ? parsed.model : undefined,
      apiKey: typeof parsed.apiKey === 'string' && parsed.apiKey ? parsed.apiKey : undefined,
      customAgents: Array.isArray(parsed.customAgents) ? parsed.customAgents.filter(isAgentDefinition).map(agent => ({ ...agent, custom: true })) : [],
      bridge: parsed.bridge === true,
      mock: parsed.mock === true,
      localAgent: parsed.localAgent === true,
      localAgentUrl: typeof parsed.localAgentUrl === 'string' && parsed.localAgentUrl ? parsed.localAgentUrl : undefined,
      localModel: typeof parsed.localModel === 'string' && parsed.localModel ? parsed.localModel : undefined,
      provider: parsed.provider === 'openai' ? 'openai' : 'claude',
      providerChosen: parsed.providerChosen === true,
      openai: parsed.openai && typeof parsed.openai === 'object' ? {
        baseUrl: typeof parsed.openai.baseUrl === 'string' && parsed.openai.baseUrl ? parsed.openai.baseUrl : undefined,
        apiKey: typeof parsed.openai.apiKey === 'string' && parsed.openai.apiKey ? parsed.openai.apiKey : undefined,
        model: typeof parsed.openai.model === 'string' && parsed.openai.model ? parsed.openai.model : undefined,
        thinking: parsed.openai.thinking === true,
      } : undefined,
    };
  } catch { return { ...DEFAULT_SETTINGS }; }
}

export function saveAgentSettings(settings: AgentSettings, storage: Pick<Storage, 'setItem'> | null = typeof localStorage === 'undefined' ? null : localStorage): void {
  try { storage?.setItem(SETTINGS_KEY, JSON.stringify(settings)); } catch { /* private mode: settings live for the session */ }
}

function isAgentDefinition(value: unknown): value is AgentDefinition {
  const agent = value as AgentDefinition;
  return !!agent && typeof agent === 'object' && typeof agent.id === 'string' && typeof agent.name === 'string' && typeof agent.model === 'string'
    && typeof agent.role === 'string' && (agent.tools === '*' || Array.isArray(agent.tools));
}

/** Every agent the user can pick or delegate to, custom ones after the built-in roles. */
export function allAgents(settings: AgentSettings): AgentDefinition[] {
  const custom = settings.customAgents.filter(agent => !BUILT_IN_AGENTS.some(built => built.id === agent.id));
  return [...BUILT_IN_AGENTS, ...custom];
}

export function findAgent(settings: AgentSettings, id: string): AgentDefinition | undefined {
  return allAgents(settings).find(agent => agent.id === id);
}

/** The model an agent actually runs on: a global override wins over a built-in default, never over a custom agent's own choice. */
export function agentModel(agent: AgentDefinition, settings: AgentSettings): string {
  return agent.custom ? agent.model : settings.model || agent.model;
}

/** The interface languages as the model is told them. */
const LANGUAGE_NAMES: Record<LanguageCode, string> = { en: 'English', ru: 'Russian' };

// i18n-ignore: text for the model; «сделай кухню», «обставь спальню» are examples of what a person types
const sharedInstructions = (): string => `You are an agent inside Home Planner Web, a browser interior editor with a 2D plan and a 3D view. You act on the user's open project through tools; the user watches the editor change live and can undo every step.

The map. Every user message starts with the project map: each room with its walls by the side they close (N = top of the plan, smaller z; S = bottom; W = left; E = right), the openings cut into each wall and the items in the room. Objects have short names — w2 a wall, o1 an opening, i3 an item — and every tool takes those names. Units are metres; x runs right, z runs down. Rotation in degrees: 0 = the front faces S (+z), 90 = E, 180 = N, 270 = W; a piece against the N wall faces S (0°), against the S wall 180°, against the W wall 90°, against the E wall 270°. An offset "@" is metres from the wall's first printed point.

How to work — act in your first reply, every call you can already make in one go, no plans or step announcements in text (the transcript shows each call); at most one short line before the first call, the summary at the end:
- Do not call get_scene when the map already tells you what you need — that is nearly always. Reach for it only for finishes (detail full), another floor or a room the map truncated.
- A whole room («сделай кухню», «обставь спальню»): add_room if there is none (typical sizes: kitchen 3×4, bedroom 3.5×4, living 4×5, bathroom 2×2.5, hall 1.5×3 m; put it beside existing rooms so walls are shared) → add_opening for a door on the wall shared with the hall or a neighbour (at "start") and a window on an outer wall → furnish_room with the preset (furniture, rug, curtains, lamps, pictures, plants and tabletop decor in one call) → set_room_finish → set_view orbit. Four to six calls, all in one reply. A room that already holds the user's furniture: keep it and add only what is missing (place_along_wall, place_beside, add_item).
- Single pieces: place_along_wall (backs to a wall, in a row — a bed, a sofa, a kitchen run, a wardrobe in a corner), place_beside (a nightstand by the bed, a coffee table before the sofa, a chair before a desk), arrange_around (chairs around a table). add_item with x/z only when the user points at a spot. These tools keep doors clear, keep pieces inside the room and warn about overlaps; fix a warning with update_item, otherwise carry on.
- Finishes: set_room_finish with the user's words (either language); it searches and applies the first match. search_materials only to offer choices; set_material only for one wall side or one item part.
- Catalog: only the ids listed below exist; furnish_room picks them itself. Prefer items marked ready; a "box" item shows as a plain block. Sizes are w×d×h in metres. A kind the catalog lacks (hob, bar stool…): say so in one line and skip it — never put an unrelated item in its place.
- A room is finished when it has its main pieces, storage, lighting, textiles (a rug, curtains on every window) and decor (pictures, plants, things on the tables). furnish_room places all of that; after it, add what the user named and stop — do not re-furnish a room that already has furniture unless asked to replace it.
- Change only what the request is about; never clear a floor or delete the user's things unless asked. Prefer doing over asking: ask one short question only when the request cannot be carried out sensibly without an answer.
- Reply in the user's language (${LANGUAGE_NAMES[language()]} by default), briefly: what you did and one suggestion. No names or ids, no narration of calls.`;

const VISION_NOTE = 'Pictures: after a whole-room job you may call capture_view once and fix what you see; otherwise skip it.';
const NO_VISION_NOTE = 'Pictures: your model does not see images — never call capture_view; tool results already report overlaps and moves.';

export interface PromptContext { catalog: Catalog; materials: readonly MaterialEntry[]; agents: readonly AgentDefinition[]; /** The model sees images (Claude, Claude Code); an OpenAI-compatible model gets a note instead of a picture. */ vision?: boolean }

/** The catalog as one compact block; it is stable between turns, so it sits in the cached prefix. */
export function catalogBlock(catalog: Catalog): string {
  if (!catalog.items.length) return 'Catalog: unavailable (search_catalog may still work later).';
  const lines = ['Catalog (id — title — w×d×h m — placement; "box" = no 3D model yet):'];
  const usable = usableCatalogItems(catalog.items);
  for (const section of catalog.sections) {
    const items = usable.filter(item => item.section === section.id);
    if (!items.length) continue;
    lines.push(`## ${section.id} — ${section.title}`);
    for (const item of items) lines.push(`${item.id} — ${item.title} — ${fmt(item.size[0])}×${fmt(item.size[2])}×${fmt(item.size[1])} — ${item.placement}${item.converted ? '' : ' — box'}`);
  }
  const orphans = usable.filter(item => !catalog.sections.some(section => section.id === item.section));
  if (orphans.length) { lines.push('## other'); for (const item of orphans) lines.push(`${item.id} — ${item.title} — ${fmt(item.size[0])}×${fmt(item.size[2])}×${fmt(item.size[1])} — ${item.placement}${item.converted ? '' : ' — box'}`); }
  return lines.join('\n');
}

export function materialsBlock(materials: readonly MaterialEntry[]): string {
  if (!materials.length) return 'Materials: the material library is not connected to the agent yet; finish surfaces with a "color".';
  const counts = new Map<string, number>();
  for (const entry of materials) { const key = `${entry.surface}/${entry.collection}`; counts.set(key, (counts.get(key) ?? 0) + 1); }
  return `Material library (${materials.length} finishes; find texture ids with search_materials): ${[...counts].map(([key, count]) => `${key} ${count}`).join(', ')}.`;
}

export function agentsBlock(agents: readonly AgentDefinition[], self: AgentDefinition): string {
  const others = agents.filter(agent => agent.id !== self.id);
  return others.length ? `Agents you can delegate to (delegate tool): ${others.map(agent => `${agent.id} — ${agent.name}: ${agent.description}`).join('; ')}.` : '';
}

export function stylesBlock(): string {
  return `Render styles (render_ai style ids): ${RENDER_STYLES.map(style => `${style.id} (${style.label})`).join(', ')}.`;
}

/** The system prompt as text blocks; the last one carries the cache breakpoint. */
export function buildSystemBlocks(agent: AgentDefinition, context: PromptContext): Array<{ type: 'text'; text: string; cache_control?: { type: 'ephemeral' } }> {
  const canDelegate = agent.tools === '*' || agent.tools.includes('agents');
  const canRender = agent.tools === '*' || agent.tools.includes('render');
  const reference = [catalogBlock(context.catalog), materialsBlock(context.materials), canRender ? stylesBlock() : '', canDelegate ? agentsBlock(context.agents, agent) : ''].filter(Boolean).join('\n\n');
  return [
    { type: 'text', text: `${sharedInstructions()}\n${context.vision === false ? NO_VISION_NOTE : VISION_NOTE}\n\nYour role: ${agent.role}` },
    { type: 'text', text: reference, cache_control: { type: 'ephemeral' } },
  ];
}
